import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Paper } from '@mui/material';
import {
  selectElement,
  deselectElement,
  updateElementPosition,
  updateElementSize,
} from '../redux/slices/canvasSlice';
import CanvasElement from './CanvasElement';

const CanvasBoard = () => {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  
  const {
    orientation,
    dimensions,
    elements,
    selectedElementId,
    background,
    zoom,
  } = useSelector((state) => state.canvas);
  
  const canvasDimensions = dimensions[orientation];
  
  // Calculate scale to fit canvas in viewport
  useEffect(() => {
    const updateScale = () => {
      if (canvasRef.current) {
        const container = canvasRef.current.parentElement;
        const containerWidth = container.clientWidth - 100;
        const containerHeight = container.clientHeight - 100;
        
        const scaleX = containerWidth / canvasDimensions.width;
        const scaleY = containerHeight / canvasDimensions.height;
        const newScale = Math.min(scaleX, scaleY, 1) * zoom;
        
        setScale(newScale);
      }
    };
    
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [canvasDimensions, zoom]);
  
  const handleCanvasClick = (e) => {
    // Deselect element if clicking on canvas background
    if (e.target === canvasRef.current) {
      dispatch(deselectElement());
    }
  };
  
  const handleElementClick = (elementId, e) => {
    e.stopPropagation();
    dispatch(selectElement(elementId));
  };
  
  const handleElementDrag = (elementId, newPosition) => {
    dispatch(updateElementPosition({ id: elementId, position: newPosition }));
  };
  
  const handleElementResize = (elementId, newSize) => {
    dispatch(updateElementSize({ id: elementId, size: newSize }));
  };
  
  const getBackgroundStyle = () => {
    switch (background.type) {
      case 'color':
        return { backgroundColor: background.value };
      case 'image':
        return {
          backgroundImage: `url(${background.value})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      case 'video':
        return {}; // Video will be rendered separately
      default:
        return { backgroundColor: '#FFFFFF' };
    }
  };
  
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#E5E5E5',
        overflow: 'auto',
        padding: 3,
      }}
    >
      <Paper
        ref={canvasRef}
        id="canvas-board"
        onClick={handleCanvasClick}
        elevation={3}
        sx={{
          position: 'relative',
          width: canvasDimensions.width,
          height: canvasDimensions.height,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          cursor: 'default',
          overflow: 'hidden',
          ...getBackgroundStyle(),
        }}
      >
        {/* Background Video */}
        {background.type === 'video' && (
          <video
            src={background.value}
            autoPlay
            loop
            muted
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
            }}
          />
        )}
        
        {/* Canvas Elements */}
        {elements.map((element) => (
          <CanvasElement
            key={element.id}
            element={element}
            isSelected={element.id === selectedElementId}
            onClick={(e) => handleElementClick(element.id, e)}
            onDrag={(newPosition) => handleElementDrag(element.id, newPosition)}
            onResize={(newSize) => handleElementResize(element.id, newSize)}
            scale={scale}
          />
        ))}
      </Paper>
    </Box>
  );
};

export default CanvasBoard;



